import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useEffect, useState } from "react";
import {
  WidgetDefinition,
  UseDataParams,
  WidgetRenderProps,
  SettingsPanelProps,
  WidgetSettings,
} from "../../../models/Dashboard";
import useFetchWidgetData from "../../../hooks/useFetchWidgetData";
import { DashboardApi } from "../../../services/api/DashboardApi";
import { getMccName } from "../../../utils/mccCodeLookup";
import { formatCurrency } from "../../../utils/FormatCurrency";

const estimateYAxisWidth = (
  labels: string[],
  minWidth: number,
  maxWidth: number
) => {
  const longest = labels.reduce((max, label) => Math.max(max, label.length), 0);
  const estimated = longest * 7 + 18;
  return Math.max(minWidth, Math.min(maxWidth, estimated));
};

const truncateLabel = (label: string, maxChars: number) => {
  if (label.length <= maxChars) {
    return label;
  }

  return `${label.slice(0, Math.max(1, maxChars - 1))}…`;
};

const createYAxisTickRenderer = (maxChars: number) => {
  return ({ x, y, payload }: any) => {
    const rawLabel = String(payload?.value ?? "");
    const displayLabel = truncateLabel(rawLabel, maxChars);

    return (
      <g transform={`translate(${x},${y})`}>
        <title>{rawLabel}</title>
        <text
          x={0}
          y={0}
          dy={4}
          textAnchor="end"
          fill="var(--dashboard-muted)"
          fontSize={11}
        >
          {displayLabel}
        </text>
      </g>
    );
  };
};

/**
 * ============================================================================
 * ACTIVATIONS AND LOADS WIDGET
 * ============================================================================
 */

type TransactionTypeCount = {
  transactionType: string;
  displayName: string;
  count: number;
  amount: number;
};

interface ActivationsAndLoadsSettings extends WidgetSettings {
  barColor: string;
  showGrid: boolean;
  topN: number;
  sortOrder: "asc" | "desc";
  dateRange: number;
}

const activationsAndLoadsDefaultSettings: ActivationsAndLoadsSettings = {
  barColor: "#2b6cb0",
  showGrid: true,
  topN: 8,
  sortOrder: "desc",
  dateRange: 1,
};

const ACTIVATION_TYPE_LABELS: Record<number, { transactionType: string; displayName: string }> = {
  1:   { transactionType: "NewCardActivation",         displayName: "New Card Activation" },
  115: { transactionType: "Reload",                    displayName: "Reload" },
  127: { transactionType: "AddPromotionalFunding",     displayName: "Add Promotional Funding" },
  470: { transactionType: "InitialPromotionalFunding", displayName: "Initial Promotional Funding" },
  512: { transactionType: "LoadTransactionSettlement", displayName: "Load Transaction Settlement" },
  526: { transactionType: "ConsumerLoadFunds",         displayName: "Consumer Load Funds" },
  532: { transactionType: "CashDepositIncomm",         displayName: "Cash Deposit Incomm" },
};

const useActivationsAndLoadsData = (
  params: UseDataParams<ActivationsAndLoadsSettings>
) => {
  const { settings } = params;
  const developer = params.globalFilters.entities?.developerUniqueTags?.[0] ?? "";
  const program = params.globalFilters.entities?.programUniqueTags?.[0];
  const merchantGroup = params.globalFilters.entities?.merchantGroupUniqueTags?.[0];

  return useFetchWidgetData({
    key: [developer, program ?? "", merchantGroup ?? "", settings.dateRange, params.refreshToken ?? 0],
    fetch: async () => {
      if (!developer) {
        return [];
      }

      const data = await DashboardApi.activationAndLoadsWidgetData(
        developer,
        program,
        merchantGroup,
        undefined, 
        settings.dateRange,
      );

      return data.transactionCounts.map((item) => {
        const mapped = ACTIVATION_TYPE_LABELS[item.systemTransactionTypeId];
        return {
          transactionType:
            mapped?.transactionType ?? `Type${item.systemTransactionTypeId}`,
          displayName:
            mapped?.displayName ?? `Type ${item.systemTransactionTypeId}`,
          count: item.transactionCount,
          amount: 0,
        };
      });
    },
  });
};

export const activationsAndLoadsWidget: WidgetDefinition<
  TransactionTypeCount[],
  ActivationsAndLoadsSettings
> = {
  id: "activations-loads",
  title: "Activations and Loads",
  description: "Transaction counts by type for the previous day",
  icon: "bi-graph-up",

  defaultSettings: activationsAndLoadsDefaultSettings,

  minWidth: 4,
  minHeight: 8,
  defaultSizes: {
    lg: { w: 4, h: 10, minW: 4, minH: 8 },
    md: { w: 5, h: 10, minW: 4, minH: 8 },
    sm: { w: 6, h: 9, minW: 4, minH: 8 },
    xs: { w: 4, h: 9, minW: 4, minH: 8 },
    xxs: { w: 2, h: 9, minW: 2, minH: 8 },
  },

  supportsExport: true,
  supportsRefresh: true,

  useData: useActivationsAndLoadsData,

  render: (props: WidgetRenderProps<TransactionTypeCount[], ActivationsAndLoadsSettings>) => {
    const { data, settings } = props;

    const sortedData = [...data].sort((a, b) =>
      settings.sortOrder === "desc" ? b.count - a.count : a.count - b.count
    );
    const chartData = sortedData.slice(0, settings.topN).map((item) => ({
      ...item,
      value: item.count,
    }));

    const yAxisWidth = estimateYAxisWidth(
      chartData.map((row) => row.displayName),
      190,
      260
    );
    const yAxisTickMaxChars = Math.max(12, Math.floor((yAxisWidth - 18) / 7));
    const yAxisTick = createYAxisTickRenderer(yAxisTickMaxChars);

    const total = chartData.reduce((sum, d) => sum + d.value, 0);

    const renderTooltip = ({ active, payload }: any) => {
      if (!active || !payload?.length) return null;
      const row = payload[0].payload as TransactionTypeCount & { value: number };
      const value = row.value;
      const pct = total > 0 ? ((value / total) * 100).toFixed(1) : "0.0";
      return (
        <div
          style={{
            background: "var(--dashboard-surface)",
            border: "1px solid var(--dashboard-border)",
            borderRadius: "8px",
            padding: "10px 14px",
            color: "var(--dashboard-text)",
            fontSize: "0.82rem",
            lineHeight: "1.7",
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{row.displayName}</div>
          <div>Count: <strong>{row.count.toLocaleString()}</strong></div>
          <div>% of Total: <strong>{pct}%</strong></div>
        </div>
      );
    };

    const chartHeight = Math.max(chartData.length * 36 + 60, 200);

    return (
      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart
          layout="vertical"
          data={chartData}
          margin={{ top: 4, right: 30, bottom: 4, left: 8 }}
        >
          {settings.showGrid && <CartesianGrid strokeDasharray="3 3" horizontal={false} />}
          <XAxis
            type="number"
            tick={{ fontSize: 11 }}
            tickFormatter={(v) => v.toLocaleString()}
          />
          <YAxis
            type="category"
            dataKey="displayName"
            width={yAxisWidth}
            tick={yAxisTick}
          />
          <Tooltip content={renderTooltip} cursor={{ fill: "var(--dashboard-border)" }} />
          <Bar dataKey="value" fill={settings.barColor ?? "#2b6cb0"} name="Count" radius={[0, 4, 4, 0]} />
        </BarChart>
      </ResponsiveContainer>
    );
  },

  SettingsPanel: (props: SettingsPanelProps<ActivationsAndLoadsSettings>) => {
    const { settings, onSettingsChange, isSaving } = props;
    const handleChange = (key: keyof ActivationsAndLoadsSettings, value: any) =>
      onSettingsChange({ ...settings, [key]: value });
    const handleResetDefaults = () =>
      onSettingsChange({ ...activationsAndLoadsDefaultSettings });

    return (
      <section className="settings-section">
        <h6>Settings</h6>

        <div className="mb-3">
          <label className="form-label" htmlFor="barColor">Bar Color</label>
          <input
            id="barColor"
            className="form-control form-control-color"
            type="color"
            value={settings.barColor}
            onChange={(e) => handleChange("barColor", e.target.value)}
            disabled={isSaving}
          />
        </div>

        <div className="mb-3">
          <label className="form-label" htmlFor="topN">Transaction Types</label>
          <select
            id="topN"
            className="form-select form-select-sm"
            value={settings.topN}
            onChange={(e) => handleChange("topN", Number(e.target.value))}
            disabled={isSaving}
          >
            <option value="5">Top 5</option>
            <option value="8">Top 8</option>
          </select>
        </div>

        <div className="mb-3">
            <label className="form-label" htmlFor="dateRange">Date Range</label>
            <select
                id="dateRange"
                className="form-select form-select-sm"
                value={settings.dateRange}
                onChange={(e) => handleChange("dateRange", Number(e.target.value))}
                disabled={isSaving}
            >
                <option value="1">Last Day</option>
                <option value="7">Last 7 Days</option>
                <option value="30">Last 30 Days</option>

            </select>
        </div>

        <div className="mb-3">
          <label className="form-label" htmlFor="sortOrder">Sort Order</label>
          <select
            id="sortOrder"
            className="form-select form-select-sm"
            value={settings.sortOrder}
            onChange={(e) => handleChange("sortOrder", e.target.value as "asc" | "desc")}
            disabled={isSaving}
          >
            <option value="desc">Highest First</option>
            <option value="asc">Lowest First</option>
          </select>
        </div>

        <div className="mb-3">
          <label className="form-label" htmlFor="showGrid">Show Grid</label>
          <div className="form-check">
            <input
              id="showGrid"
              className="form-check-input"
              type="checkbox"
              checked={settings.showGrid}
              onChange={(e) => handleChange("showGrid", e.target.checked)}
              disabled={isSaving}
            />
            <label className="form-check-label" htmlFor="showGrid">
              Enabled
            </label>
          </div>
        </div>

        <div className="d-flex justify-content-end">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={handleResetDefaults}
            disabled={isSaving}
          >
            Reset to defaults
          </button>
        </div>
      </section>
    );
  },
};

/**
 * ============================================================================
 * MCC TOP N WIDGET
 * ============================================================================
 */

type MCCData = {
  mccCode: string;
  mccName: string;
  count: number;
};

interface MCCTopNSettings extends WidgetSettings {
  topN: number;
  sortOrder: "asc" | "desc";
  barColor: string;
  dateRange: number;
}

const useMCCTopNData = (params: UseDataParams<MCCTopNSettings>) => {
  const { settings, globalFilters,} = params;
  const developer = globalFilters.entities?.developerUniqueTags?.[0] ?? "";
  const program = globalFilters.entities?.programUniqueTags?.[0];
  const merchantGroup = globalFilters.entities?.merchantGroupUniqueTags?.[0];

  return useFetchWidgetData({
    key: [developer, program ?? "", merchantGroup ?? "", settings.topN, settings.sortOrder, settings.dateRange, params.refreshToken ?? 0],
    useCache: false,
    fetch: async () => {
      if (!developer) {
        return [];
      }

      const data = await DashboardApi.mccWidgetData(
        developer,
        program,
        merchantGroup,
        undefined,
        settings.topN,
        settings.dateRange
      );

      const mapped: MCCData[] = data.transactionCounts.map((item) => {
        const mccCode = item.merchantTypeCode;
        return {
          mccCode,
          mccName: getMccName(mccCode),
          count: item.transactionCount,
        };
      });

      return mapped.sort((a, b) =>
        settings.sortOrder === "desc" ? b.count - a.count : a.count - b.count
      );
    },
  });
};

export const mccTopNWidget: WidgetDefinition<MCCData[], MCCTopNSettings> = {
  id: "mcc-top-n",
  title: "MCC Top N",
  description: "Top merchant category codes by volume",
  icon: "bi-list-ul",

  defaultSettings: {
    topN: 20,
    dateRange: 1,
    sortOrder: "desc",
    barColor: "#2b6cb0",
  },

  minWidth: 4,
  minHeight: 8,
  defaultSizes: {
      lg: { w: 4, h: 20, minW: 4, minH: 8 },
      md: { w: 5, h: 20, minW: 4, minH: 8 },
      sm: { w: 6, h: 9, minW: 4, minH: 8 },
      xs: { w: 4, h: 9, minW: 4, minH: 8 },
      xxs: { w: 2, h: 9, minW: 2, minH: 8 },
  },

  supportsExport: true,
  supportsRefresh: true,

  useData: useMCCTopNData,

  render: (props: WidgetRenderProps<MCCData[], MCCTopNSettings>) => {
    const { data, settings } = props;

    const total = data.reduce((sum, d) => sum + d.count, 0);

    const chartData = data.map((item) => ({
      ...item,
      label: `${item.mccCode} - ${item.mccName}`,
      value: item.count,
    }));

    const renderTooltip = ({ active, payload }: any) => {
      if (!active || !payload?.length) return null;
      const row = payload[0].payload as (typeof chartData)[0];
      const pct = total > 0 ? ((row.value / total) * 100).toFixed(1) : "0.0";
      return (
        <div
          style={{
            background: "var(--dashboard-surface)",
            border: "1px solid var(--dashboard-border)",
            borderRadius: "8px",
            padding: "10px 14px",
            color: "var(--dashboard-text)",
            fontSize: "0.82rem",
            lineHeight: "1.7",
          }}
        >
          <div style={{ marginBottom: 4 }}>
            <strong>{row.mccCode}</strong> - {row.mccName}
          </div>
          <div>
            Count: <strong>{row.value.toLocaleString()}</strong>
          </div>
          <div>
            % of Total: <strong>{pct}%</strong>
          </div>
        </div>
      );
    };

    const chartHeight = Math.max(chartData.length * 32 + 60, 200);
    const yAxisWidth = estimateYAxisWidth(
      chartData.map((row) => row.label),
      210,
      280
    );
    const yAxisTickMaxChars = Math.max(14, Math.floor((yAxisWidth - 18) / 7));
    const yAxisTick = createYAxisTickRenderer(yAxisTickMaxChars);

    return (
      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart
          layout="vertical"
          data={chartData}
          margin={{ top: 4, right: 30, bottom: 4, left: 8 }}
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis
            type="number"
            tick={{ fontSize: 11 }}
            tickFormatter={(v) => v.toLocaleString()}
          />
          <YAxis
            type="category"
            dataKey="label"
            width={yAxisWidth}
            tick={yAxisTick}
          />
          <Tooltip content={renderTooltip} cursor={{ fill: "var(--dashboard-border)" }} />
          <Bar dataKey="value" fill={settings.barColor ?? "#2b6cb0"} radius={[0, 4, 4, 0]} />
        </BarChart>
      </ResponsiveContainer>
    );
  },
};

/**
 * ============================================================================
 * MERCHANT TOP N WIDGET
 * ============================================================================
 */

type MerchantData = {
  merchant: string;
  count: number;
};

interface MerchantTopNSettings extends WidgetSettings {
  topN: number;
  sortOrder: "asc" | "desc";
  barColor: string;
  dateRange: number;
}

const useMerchantTopNData = (params: UseDataParams<MerchantTopNSettings>) => {
  const { settings, globalFilters, } = params;
  const developer = globalFilters.entities?.developerUniqueTags?.[0] ?? "";
  const program = globalFilters.entities?.programUniqueTags?.[0];
  const merchantGroup = globalFilters.entities?.merchantGroupUniqueTags?.[0];

  return useFetchWidgetData({
    key: [developer, program ?? "", merchantGroup ?? "", settings.topN, settings.sortOrder, settings.dateRange, params.refreshToken ?? 0],
    useCache: false,
    fetch: async () => {
      if (!developer) {
        return [];
      }

      const data = await DashboardApi.merchantWidgetData(
        developer,
        program,
        merchantGroup,
        undefined,
        settings.topN,
        settings.dateRange
      );

      const mapped: MerchantData[] = data.transactionCounts.map((item) => ({
        merchant: item.merchant,
        count: item.transactionCount,
      }));

      return mapped.sort((a, b) =>
        settings.sortOrder === "desc" ? b.count - a.count : a.count - b.count
      );
    },
  });
};

export const merchantTopNWidget: WidgetDefinition<
  MerchantData[],
  MerchantTopNSettings
> = {
  id: "merchant-top-n",
  title: "Merchant Top N",
  description: "Top merchants by transaction volume",
  icon: "bi-shop",

  defaultSettings: {
    topN: 20,
    dateRange: 1,
    sortOrder: "desc",
    barColor: "#0e7c55",
  },

  minWidth: 4,
  minHeight: 8,
  defaultSizes: {
      lg: { w: 4, h: 20, minW: 4, minH: 8 },
      md: { w: 5, h: 20, minW: 4, minH: 8 },
      sm: { w: 6, h: 9, minW: 4, minH: 8 },
      xs: { w: 4, h: 9, minW: 4, minH: 8 },
      xxs: { w: 2, h: 9, minW: 2, minH: 8 },
  },

  supportsExport: true,
  supportsRefresh: true,

  useData: useMerchantTopNData,

  render: (props: WidgetRenderProps<MerchantData[], MerchantTopNSettings>) => {
    const { data, settings } = props;

    const total = data.reduce((sum, d) => sum + d.count, 0);

    const chartData = data.map((item) => ({
      ...item,
      label: item.merchant,
      value: item.count,
    }));

    const renderTooltip = ({ active, payload }: any) => {
      if (!active || !payload?.length) return null;
      const row = payload[0].payload as (typeof chartData)[0];
      const pct = total > 0 ? ((row.value / total) * 100).toFixed(1) : "0.0";
      return (
        <div
          style={{
            background: "var(--dashboard-surface)",
            border: "1px solid var(--dashboard-border)",
            borderRadius: "8px",
            padding: "10px 14px",
            color: "var(--dashboard-text)",
            fontSize: "0.82rem",
            lineHeight: "1.7",
            maxWidth: 260,
          }}
        >
          <div style={{ marginBottom: 4, fontWeight: 600 }}>{row.merchant}</div>
          <div>Count: <strong>{row.value.toLocaleString()}</strong></div>
          <div>% of Total: <strong>{pct}%</strong></div>
        </div>
      );
    };

    const chartHeight = Math.max(chartData.length * 32 + 60, 200);
    const yAxisWidth = estimateYAxisWidth(
      chartData.map((row) => row.label),
      140,
      260
    );
    const yAxisTickMaxChars = Math.max(10, Math.floor((yAxisWidth - 18) / 7));
    const yAxisTick = createYAxisTickRenderer(yAxisTickMaxChars);

    return (
      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart
          layout="vertical"
          data={chartData}
          margin={{ top: 4, right: 30, bottom: 4, left: 8 }}
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis
            type="number"
            tick={{ fontSize: 11 }}
            tickFormatter={(v) => v.toLocaleString()}
          />
          <YAxis
            type="category"
            dataKey="label"
            width={yAxisWidth}
            tick={yAxisTick}
          />
          <Tooltip content={renderTooltip} cursor={{ fill: "var(--dashboard-border)" }} />
          <Bar dataKey="value" fill={settings.barColor ?? "#0e7c55"} radius={[0, 4, 4, 0]} />
        </BarChart>
      </ResponsiveContainer>
    );
  },
};

/**
 * ============================================================================
 * SETTLEMENT VOLUME WIDGET
 * ============================================================================
 */

interface SettlementMetric {
  amount: number;
  count: number;
}

type SettlementCurrencyData = {
  currencyAlphaCode: string;
  targetDayPurchases: SettlementMetric;
  targetDayReturns: SettlementMetric;
  previousDayPurchases: SettlementMetric;
  previousDayReturns: SettlementMetric;
};

type SettlementData = {
  currencies: SettlementCurrencyData[];
} | null;

type SettlementDeltaData = {
  current: number;
  previous: number;
  absolute: number;
  percent: number;
  direction: "up" | "down" | "flat";
};

type SettlementVolumeContentProps = {
  data: Exclude<SettlementData, null>;
  settings: SettlementVolumeSettings;
};

interface SettlementVolumeSettings extends WidgetSettings {
  showMetric: "purchases" | "returns" | "both";
  purchasesColor: string;
  returnsColor: string;
  dateRange: number;
}

const settlementVolumeDefaultSettings: SettlementVolumeSettings = {
  showMetric: "both",
  purchasesColor: "#ff9800",
  returnsColor: "#2196f3",
  dateRange: 1
};

const getSettlementDelta = (currentValue?: number, previousValue?: number) => {
  if (typeof currentValue !== "number" || typeof previousValue !== "number") {
    return null;
  }

  const absolute = currentValue - previousValue;
  const percent = previousValue === 0
    ? (absolute === 0 ? 0 : 100)
    : (absolute / previousValue) * 100;

  return {
    current: currentValue,
    previous: previousValue,
    absolute,
    percent,
    direction: absolute > 0 ? "up" : absolute < 0 ? "down" : "flat",
  } as const;
};

const formatSettlementVolume = (value: number, currencyAlphaCode: string) =>
  String(formatCurrency(value, currencyAlphaCode || "USD"));

const formatSettlementCount = (value: number) =>
  new Intl.NumberFormat("en-US").format(value);

const formatSignedValue = (
  value: number,
  formatter: (input: number) => string
) => {
  const prefix = value > 0 ? "+" : value < 0 ? "-" : "";
  return `${prefix}${formatter(Math.abs(value))}`;
};

const formatSignedPercent = (value: number) => {
  const prefix = value > 0 ? "+" : value < 0 ? "-" : "";
  return `${prefix}${Math.abs(value).toFixed(1)}%`;
};

const formatMetricValue = (
  valueType: "amount" | "count",
  value: number,
  currencyAlphaCode: string
) =>
  valueType === "amount"
    ? formatSettlementVolume(value, currencyAlphaCode)
    : formatSettlementCount(value);

const buildDeltaAriaLabel = (
  metricLabel: string,
  valueType: "amount" | "count",
  currencyAlphaCode: string,
  delta: SettlementDeltaData
) => {
  const metric = metricLabel.toLowerCase();
  const absoluteValue = formatMetricValue(valueType, Math.abs(delta.absolute), currencyAlphaCode);
  const percentAbs = Math.abs(delta.percent).toFixed(1);

  if (delta.direction === "flat") {
    return `${metric} was unchanged, 0.0 percent vs previous day.`;
  }

  return `${metric} ${delta.direction === "up" ? "increased" : "decreased"} by ${absoluteValue}, ${delta.direction === "up" ? "up" : "down"} ${percentAbs} percent vs previous day.`;
};

type SettlementMetricCardProps = {
  label: string;
  amount: number;
  count: number;
  icon: string;
  color: string;
  currencyAlphaCode: string;
  amountDelta: SettlementDeltaData | null;
  countDelta: SettlementDeltaData | null;
};

const SettlementMetricCard = ({
  label,
  amount,
  count,
  icon,
  color,
  currencyAlphaCode,
  amountDelta,
  countDelta,
}: SettlementMetricCardProps) => {
  const amountAbsoluteDisplay = amountDelta
    ? formatSignedValue(amountDelta.absolute, (input) => formatSettlementVolume(input, currencyAlphaCode))
    : "";
  const amountPercentDisplay = amountDelta ? formatSignedPercent(amountDelta.percent) : "";
  const countAbsoluteDisplay = countDelta
    ? formatSignedValue(countDelta.absolute, (input) => formatSettlementCount(input))
    : "";
  const countPercentDisplay = countDelta ? formatSignedPercent(countDelta.percent) : "";

  const amountDeltaTooltipRows = amountDelta
    ? [
        `Current: ${formatSettlementVolume(amountDelta.current, currencyAlphaCode)}`,
        `Previous: ${formatSettlementVolume(amountDelta.previous, currencyAlphaCode)}`,
        `Absolute Δ: ${amountAbsoluteDisplay}`,
        `Percent Δ: ${amountPercentDisplay}`,
      ]
    : [];

  const countDeltaTooltipRows = countDelta
    ? [
        `Current: ${formatSettlementCount(countDelta.current)}`,
        `Previous: ${formatSettlementCount(countDelta.previous)}`,
        `Absolute Δ: ${countAbsoluteDisplay}`,
        `Percent Δ: ${countPercentDisplay}`,
      ]
    : [];

  const amountDeltaAriaLabel = amountDelta
    ? buildDeltaAriaLabel(`${label} Amount`, "amount", currencyAlphaCode, amountDelta)
    : `${label.toLowerCase()} amount baseline unavailable. Previous day data not available.`;

  const countDeltaAriaLabel = countDelta
    ? buildDeltaAriaLabel(`${label} Count`, "count", currencyAlphaCode, countDelta)
    : `${label.toLowerCase()} count baseline unavailable. Previous day data not available.`;

  return (
    <div
      className="settlement-metric-card"
      style={{
        borderTopColor: color,
        background: `color-mix(in srgb, ${color} 10%, var(--dashboard-surface))`,
      }}
    >
      <div className="settlement-metric-header">
        <span className="settlement-metric-label">{label}</span>
        <i className={`bi ${icon} settlement-metric-icon`} style={{ color }} aria-hidden="true" />
      </div>

      <div className="settlement-metric-subsection">
        <div className="settlement-metric-sublabel">Amount</div>
        <div className="settlement-metric-value" style={{ color }}>
          {formatSettlementVolume(amount, currencyAlphaCode)}
        </div>
        {amountDelta && (
          <div
            className={`settlement-metric-delta settlement-metric-delta--${amountDelta.direction}`}
            aria-label={amountDeltaAriaLabel}
            tabIndex={0}
          >
            <i
              className={`bi ${
                amountDelta.direction === "up"
                  ? "bi-arrow-up-right"
                  : amountDelta.direction === "down"
                    ? "bi-arrow-down-right"
                    : "bi-dash"
              }`}
              aria-hidden="true"
            />
            <span className="settlement-metric-delta__primary">
              {amountDelta.direction === "flat"
                ? `No change (${amountPercentDisplay})`
                : `${amountAbsoluteDisplay} (${amountPercentDisplay})`}
            </span>
            <span className="settlement-metric-delta__secondary">vs previous day</span>
            <div className="settlement-metric-delta-tooltip" role="tooltip">
              {amountDeltaTooltipRows.map((line) => (
                <div key={line}>{line}</div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="settlement-metric-subsection">
        <div className="settlement-metric-sublabel">Count</div>
        <div className="settlement-metric-value" style={{ color }}>
          {formatSettlementCount(count)}
        </div>
        {countDelta && (
          <div
            className={`settlement-metric-delta settlement-metric-delta--${countDelta.direction}`}
            aria-label={countDeltaAriaLabel}
            tabIndex={0}
          >
            <i
              className={`bi ${
                countDelta.direction === "up"
                  ? "bi-arrow-up-right"
                  : countDelta.direction === "down"
                    ? "bi-arrow-down-right"
                    : "bi-dash"
              }`}
              aria-hidden="true"
            />
            <span className="settlement-metric-delta__primary">
              {countDelta.direction === "flat"
                ? `No change (${countPercentDisplay})`
                : `${countAbsoluteDisplay} (${countPercentDisplay})`}
            </span>
            <span className="settlement-metric-delta__secondary">vs previous day</span>
            <div className="settlement-metric-delta-tooltip" role="tooltip">
              {countDeltaTooltipRows.map((line) => (
                <div key={line}>{line}</div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const useSettlementVolumeData = (
  params: UseDataParams<SettlementVolumeSettings>
) => {
  const { settings, globalFilters, } = params;
  const developer = globalFilters.entities?.developerUniqueTags?.[0] ?? "";
  const program = globalFilters.entities?.programUniqueTags?.[0];
  const merchantGroup = globalFilters.entities?.merchantGroupUniqueTags?.[0];

  return useFetchWidgetData({
    key: [developer, program ?? "", merchantGroup ?? "", settings.dateRange, params.refreshToken ?? 0],
    fetch: async () => {
      if (!developer) {
        return null;
      }

      const apiData = await DashboardApi.settlementWidgetData(developer, program, merchantGroup, undefined, settings.dateRange);

      const createEmptyMetrics = (): SettlementCurrencyData => ({
        currencyAlphaCode: "USD",
        targetDayPurchases: { amount: 0, count: 0 },
        targetDayReturns: { amount: 0, count: 0 },
        previousDayPurchases: { amount: 0, count: 0 },
        previousDayReturns: { amount: 0, count: 0 },
      });

      const isValidCurrency = (code: string | undefined | null): code is string =>
        typeof code === "string" && code.trim().length === 3;

      const byCurrency = new Map<string, SettlementCurrencyData>();

      const getCurrencyBucket = (rawCode: string | undefined | null) => {
        const code = isValidCurrency(rawCode) ? rawCode.toUpperCase() : "USD";
        const existing = byCurrency.get(code);
        if (existing) {
          return existing;
        }

        const created = createEmptyMetrics();
        created.currencyAlphaCode = code;
        byCurrency.set(code, created);
        return created;
      };

      for (const item of apiData.targetDayPurchases) {
        const bucket = getCurrencyBucket(item.currencyAlphaCode);
        bucket.targetDayPurchases.amount += item.transactionAmount;
        bucket.targetDayPurchases.count += item.transactionCount;
      }

      for (const item of apiData.targetDayPurchaseReturns) {
        const bucket = getCurrencyBucket(item.currencyAlphaCode);
        bucket.targetDayReturns.amount += item.transactionAmount;
        bucket.targetDayReturns.count += item.transactionCount;
      }

      for (const item of apiData.previousDayPurchases) {
        const bucket = getCurrencyBucket(item.currencyAlphaCode);
        bucket.previousDayPurchases.amount += item.transactionAmount;
        bucket.previousDayPurchases.count += item.transactionCount;
      }

      for (const item of apiData.previousDayPurchaseReturns) {
        const bucket = getCurrencyBucket(item.currencyAlphaCode);
        bucket.previousDayReturns.amount += item.transactionAmount;
        bucket.previousDayReturns.count += item.transactionCount;
      }

      const currencies = [...byCurrency.values()]
        .filter((currency) =>
          currency.targetDayPurchases.amount !== 0 ||
          currency.targetDayReturns.amount !== 0 ||
          currency.previousDayPurchases.amount !== 0 ||
          currency.previousDayReturns.amount !== 0 ||
          currency.targetDayPurchases.count !== 0 ||
          currency.targetDayReturns.count !== 0 ||
          currency.previousDayPurchases.count !== 0 ||
          currency.previousDayReturns.count !== 0
        )
        .sort((a, b) => a.currencyAlphaCode.localeCompare(b.currencyAlphaCode));

      if (currencies.length === 0) {
        return null;
      }

      return { currencies };
    },
  });
};

const SettlementVolumeContent = ({
  data,
  settings,
}: SettlementVolumeContentProps) => {
  const [selectedCurrencyCode, setSelectedCurrencyCode] = useState(
    data.currencies[0]?.currencyAlphaCode ?? ""
  );

  useEffect(() => {
    if (!data.currencies.some((currency) => currency.currencyAlphaCode === selectedCurrencyCode)) {
      setSelectedCurrencyCode(data.currencies[0]?.currencyAlphaCode ?? "");
    }
  }, [data.currencies, selectedCurrencyCode]);

  const selectedCurrencyData =
    data.currencies.find((currency) => currency.currencyAlphaCode === selectedCurrencyCode) ??
    data.currencies[0];

  if (!selectedCurrencyData) {
    return null;
  }

  const showPurchases = settings.showMetric === "purchases" || settings.showMetric === "both";
  const showReturns = settings.showMetric === "returns" || settings.showMetric === "both";

  const purchasesAmountDelta = getSettlementDelta(
    selectedCurrencyData.targetDayPurchases.amount,
    selectedCurrencyData.previousDayPurchases.amount
  );
  const purchasesCountDelta = getSettlementDelta(
    selectedCurrencyData.targetDayPurchases.count,
    selectedCurrencyData.previousDayPurchases.count
  );
  const returnsAmountDelta = getSettlementDelta(
    selectedCurrencyData.targetDayReturns.amount,
    selectedCurrencyData.previousDayReturns.amount
  );
  const returnsCountDelta = getSettlementDelta(
    selectedCurrencyData.targetDayReturns.count,
    selectedCurrencyData.previousDayReturns.count
  );

  return (
    <div className="settlement-currency-view">
      {data.currencies.length > 1 && (
        <div className="settlement-currency-tabs" role="tablist" aria-label="Settlement currencies">
          {data.currencies.map((currency) => {
            const isActive = currency.currencyAlphaCode === selectedCurrencyCode;
            return (
              <button
                key={currency.currencyAlphaCode}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`settlement-currency-tab${isActive ? " settlement-currency-tab--active" : ""}`}
                onClick={() => setSelectedCurrencyCode(currency.currencyAlphaCode)}
              >
                {currency.currencyAlphaCode}
              </button>
            );
          })}
        </div>
      )}

      <div className={`settlement-metric-cards${settings.showMetric !== "both" ? " settlement-metric-cards--single" : ""}`}>
        {showPurchases && (
          <SettlementMetricCard
            label="Purchases"
            amount={selectedCurrencyData.targetDayPurchases.amount}
            count={selectedCurrencyData.targetDayPurchases.count}
            icon="bi-cart-check"
            color={settings.purchasesColor}
            currencyAlphaCode={selectedCurrencyData.currencyAlphaCode}
            amountDelta={purchasesAmountDelta}
            countDelta={purchasesCountDelta}
          />
        )}
        {showReturns && (
          <SettlementMetricCard
            label="Returns"
            amount={selectedCurrencyData.targetDayReturns.amount}
            count={selectedCurrencyData.targetDayReturns.count}
            icon="bi-arrow-counterclockwise"
            color={settings.returnsColor}
            currencyAlphaCode={selectedCurrencyData.currencyAlphaCode}
            amountDelta={returnsAmountDelta}
            countDelta={returnsCountDelta}
          />
        )}
      </div>
    </div>
  );
};

export const settlementVolumeWidget: WidgetDefinition<
  SettlementData,
  SettlementVolumeSettings
> = {
  id: "settlement-volume",
  title: "Settlement Volume",
  description: "Settlement volumes and counts for purchases and returns",
  icon: "bi-cash-stack",

  defaultSettings: settlementVolumeDefaultSettings,

  minWidth: 4,
  minHeight: 8,
  defaultSizes: {
      lg: { w: 4, h: 10, minW: 4, minH: 8 },
      md: { w: 5, h: 10, minW: 4, minH: 8 },
      sm: { w: 6, h: 9, minW: 4, minH: 8 },
      xs: { w: 4, h: 9, minW: 4, minH: 8 },
      xxs: { w: 2, h: 9, minW: 2, minH: 8 },
  },

  supportsExport: true,
  supportsRefresh: true,

  useData: useSettlementVolumeData,
  render: (props: WidgetRenderProps<SettlementData, SettlementVolumeSettings>) => {
    const { data, settings } = props;

    if (!data) {
      return null;
    }

    return <SettlementVolumeContent data={data} settings={settings} />;
  },

  SettingsPanel: (props: SettingsPanelProps<SettlementVolumeSettings>) => {
    const { settings, onSettingsChange, isSaving } = props;
    const handleChange = (key: keyof SettlementVolumeSettings, value: any) =>
      onSettingsChange({ ...settings, [key]: value });
    const handleResetDefaults = () =>
      onSettingsChange({ ...settlementVolumeDefaultSettings });

    const showPurchases = settings.showMetric === "purchases" || settings.showMetric === "both";
    const showReturns = settings.showMetric === "returns" || settings.showMetric === "both";

    return (
      <section className="settings-section">
        <h6>Settings</h6>
        <div className="mb-3">
          <label className="form-label" htmlFor="showMetric">Show Metric</label>
          <select
            id="showMetric"
            className="form-select form-select-sm"
            value={settings.showMetric}
            onChange={(e) => handleChange("showMetric", e.target.value)}
            disabled={isSaving}
          >
            <option value="purchases">Purchases</option>
            <option value="returns">Returns</option>
            <option value="both">Both</option>
          </select>
        </div>
        <div className="mb-3">
            <label className="form-label" htmlFor="dateRange">Date Range</label>
            <select
                id="dateRange"
                className="form-select form-select-sm"
                value={settings.dateRange}
                onChange={(e) => handleChange("dateRange", Number(e.target.value))}
                disabled={isSaving}
            >
                <option value="1">Last Day</option>
                <option value="7">Last 7 Days</option>
                <option value="30">Last 30 Days</option>
        
            </select>
        </div>
        {showPurchases && (
          <div className="mb-3">
            <label className="form-label" htmlFor="purchasesColor">Purchases Color</label>
            <input
              id="purchasesColor"
              className="form-control form-control-color"
              type="color"
              value={settings.purchasesColor}
              onChange={(e) => handleChange("purchasesColor", e.target.value)}
              disabled={isSaving}
            />
          </div>
        )}
        {showReturns && (
          <div className="mb-3">
            <label className="form-label" htmlFor="returnsColor">Returns Color</label>
            <input
              id="returnsColor"
              className="form-control form-control-color"
              type="color"
              value={settings.returnsColor}
              onChange={(e) => handleChange("returnsColor", e.target.value)}
              disabled={isSaving}
            />
          </div>
        )}

        <div className="d-flex justify-content-end">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={handleResetDefaults}
            disabled={isSaving}
          >
            Reset to defaults
          </button>
        </div>
      </section>
    );
  },
};

/**
 * ============================================================================
 * WIDGET REGISTRY
 * ============================================================================
 */

export const widgetDefinitions = [
  activationsAndLoadsWidget,
  mccTopNWidget,
  merchantTopNWidget,
  settlementVolumeWidget,
];
